import React, { useMemo } from 'react';
import { PencilIcon, TrashIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import {
  AvailabilityWorkModels,
  type AvailabilityFormSlotInput,
  type AvailabilityWorkModel,
} from '../../models';
import { AllocationBucketTypes, type AllocationBucketType } from '@/features/Collaborators/Models';

interface AvailabilitySlotCardProps {
  index: number;
  slot: AvailabilityFormSlotInput;
  onEdit: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
  bucketMetadata: Record<AllocationBucketType, { label: string; description: string }>;
  workModelMetadata: Record<AvailabilityWorkModel, { label: string }>;
}

export function AvailabilitySlotCard({
  index,
  slot,
  onEdit,
  onDuplicate,
  onRemove,
  bucketMetadata,
  workModelMetadata,
}: AvailabilitySlotCardProps) {
  const bucketMeta = bucketMetadata[slot.bucketType];
  const workModelMeta = workModelMetadata[slot.workModel];

  const primaryCapability = useMemo(() => {
    if (!slot.capabilities || slot.capabilities.length === 0) {
      return null;
    }
    return slot.capabilities.find((capability) => capability?.isPrimary) ?? slot.capabilities[0];
  }, [slot.capabilities]);

  const primaryCapabilityLabel = useMemo(() => {
    if (!primaryCapability) {
      return null;
    }

    return [primaryCapability.typeName, primaryCapability.technologyName, primaryCapability.seniorityName]
      .filter(Boolean)
      .join(' • ');
  }, [primaryCapability]);

  const additionalCapabilitiesCount = useMemo(() => {
    if (!slot.capabilities) {
      return 0;
    }
    if (!primaryCapability) {
      return slot.capabilities.length;
    }
    return Math.max(slot.capabilities.length - 1, 0);
  }, [primaryCapability, slot.capabilities]);

  const selectedCandidates = useMemo(
    () => (slot.candidates ?? []).filter((candidate) => candidate?.isChosen),
    [slot.candidates]
  );

  const chosenCandidate = useMemo(() => {
    if (!slot.chosenCollaboratorId) {
      return null;
    }
    return (
      selectedCandidates.find(
        (candidate) => candidate?.collaboratorId === slot.chosenCollaboratorId
      ) ??
      (slot.candidates ?? []).find(
        (candidate) => candidate?.collaboratorId === slot.chosenCollaboratorId
      ) ??
      null
    );
  }, [selectedCandidates, slot.candidates, slot.chosenCollaboratorId]);

  return (
    <article className="group relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-all hover:border-indigo-300 hover:shadow-md">
      <header className="flex items-start justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">
            {slot.title?.trim() ? slot.title : `Vaga ${index + 1}`}
          </h4>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span>{bucketMeta?.label ?? 'Dedicação não definida'}</span>
          </div>
          {primaryCapabilityLabel ? (
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
              <span>{primaryCapabilityLabel}</span>
              {additionalCapabilitiesCount > 0 ? (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                  +{additionalCapabilitiesCount}
                </span>
              ) : null}
            </div>
          ) : (
            <div className="mt-2 text-xs text-gray-400">Nenhuma habilidade definida</div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-full bg-indigo-50 p-1 text-indigo-600 transition-colors hover:bg-indigo-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            onClick={onEdit}
            aria-label={`Editar vaga ${index + 1}`}
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="rounded-full bg-indigo-50 p-1 text-indigo-600 transition-colors hover:bg-indigo-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            onClick={onDuplicate}
            aria-label={`Duplicar vaga ${index + 1}`}
          >
            <DocumentDuplicateIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="rounded-full bg-red-50 p-1 text-red-600 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            onClick={onRemove}
            aria-label={`Remover vaga ${index + 1}`}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </header>

      <dl className="mt-4 space-y-3 text-xs">
        <div className="flex items-center justify-between">
          <dt className="text-gray-500">Dedicação</dt>
          <dd className="font-medium text-gray-900">
            {(() => {
              if (!Number.isFinite(slot.bucketValue)) {
                return 'Não informado';
              }

              const amount = Number(slot.bucketValue ?? 0);
              const bucketType = slot.bucketType;

              if (bucketType === AllocationBucketTypes.Daily) {
                return `${amount}h dia`;
              }
              if (bucketType === AllocationBucketTypes.Weekly) {
                return `${amount}h semana`;
              }
              if (bucketType === AllocationBucketTypes.Monthly) {
                return `${amount}h mês`;
              }

              return `${amount}h`;
            })()}
          </dd>
        </div>

        <div className="flex items-center justify-between">
          <dt className="text-gray-500">Modelo</dt>
          <dd className="font-medium text-gray-900">{workModelMeta?.label ?? 'Não informado'}</dd>
        </div>

        <div className="flex items-center justify-between">
          <dt className="text-gray-500">Local</dt>
          <dd className="font-medium text-gray-900 text-right">
            {slot.workModel === AvailabilityWorkModels.Remote
              ? 'Remoto'
              : slot.workCity
              ? `${slot.workCity}${slot.workState ? ` - ${slot.workState}` : ''}`
              : 'Não informado'}
          </dd>
        </div>

        <div>
          <dt className="text-gray-500">Capacidades</dt>
          <dd className="mt-1 flex flex-wrap gap-1">
            {slot.capabilities.length ? (
              slot.capabilities.slice(0, 3).map((capability) => {
                const primaryLabel =
                  [capability.typeName, capability.technologyName].filter(Boolean).join(' • ') ||
                  capability.typeName ||
                  'Capacidade';
                const seniorityLabel = capability.seniorityName ?? '';
                return (
                  <span
                    key={`${capability.typeId}-${capability.technologyId ?? 'any'}-${capability.seniorityId ?? 'any'}`}
                    className={`inline-flex items-center gap-2 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                      capability.required
                        ? 'border-indigo-200 bg-indigo-100 text-indigo-700'
                        : 'border-gray-200 bg-gray-100 text-gray-600'
                    }`}
                  >
                    {capability.isPrimary ? (
                      <StarSolidIcon className="h-3 w-3 text-amber-500" />
                    ) : null}
                    <span className="flex items-center gap-1">
                      <span>{primaryLabel}</span>
                      {seniorityLabel ? (
                        <span className="rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                          {seniorityLabel}
                        </span>
                      ) : null}
                    </span>
                  </span>
                );
              })
            ) : (
              <span className="text-gray-400">Nenhuma capacidade definida</span>
            )}
            {slot.capabilities.length > 3 ? (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
                +{slot.capabilities.length - 3}
              </span>
            ) : null}
          </dd>
        </div>

        <div>
          <dt className="text-gray-500">Colaboradores</dt>
          <dd className="mt-1 space-y-2">
            {(() => {
              if (chosenCandidate) {
                const initials = (chosenCandidate.collaboratorName ?? '??')
                  .slice(0, 2)
                  .toUpperCase();

                return (
                  <div className="flex flex-col gap-2 rounded-lg border border-indigo-100 bg-indigo-50 p-3">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
                      Colaborador escolhido
                    </span>
                    <div className="flex items-center gap-3">
                      {chosenCandidate.collaboratorAvatar ? (
                        <img
                          src={chosenCandidate.collaboratorAvatar}
                          alt={chosenCandidate.collaboratorName ?? 'Colaborador escolhido'}
                          className="h-9 w-9 rounded-full object-cover shadow"
                        />
                      ) : (
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-600">
                          {initials}
                        </span>
                      )}
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-900">
                          {chosenCandidate.collaboratorName ?? 'Colaborador sem nome'}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-600">
                          <StarSolidIcon className="h-3 w-3" /> Escolhido
                        </span>
                      </div>
                    </div>
                  </div>
                );
              }

              if (!selectedCandidates.length) {
                return null;
              }

              return (
                <div className="flex flex-wrap gap-2">
                  {selectedCandidates.slice(0, 3).map((candidate) => {
                    const initials = (candidate?.collaboratorName ?? '??').slice(0, 2).toUpperCase();
                    return (
                      <div
                        key={candidate?.collaboratorId ?? candidate?.collaboratorName}
                        className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 shadow-sm"
                      >
                        {candidate?.collaboratorAvatar ? (
                          <img
                            src={candidate.collaboratorAvatar}
                            alt={candidate.collaboratorName ?? 'Colaborador selecionado'}
                            className="h-6 w-6 rounded-full object-cover"
                          />
                        ) : (
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-semibold text-indigo-600">
                            {initials}
                          </span>
                        )}
                        <span className="max-w-[120px] truncate">
                          {candidate?.collaboratorName ?? 'Sem nome'}
                        </span>
                      </div>
                    );
                  })}
                  {selectedCandidates.length > 3 ? (
                    <span className="text-[11px] font-medium text-gray-500">
                      +{selectedCandidates.length - 3} outros
                    </span>
                  ) : null}
                </div>
              );
            })()}
          </dd>
        </div>
      </dl>
    </article>
  );
}
